import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    wildcardFind, wildcardReplace, wildcardReplaceAll,
    countMatches, buildRegex, getCurrentMatchIndex, jumpToMatchIndex
} from "./WildcardUtils";

// ── Styles (unchanged from your existing S object) ──────────────────────────
const S = {
    // ... your existing styles ...
    // Add one new style for the index input:
    indexInput: {
        width: "32px",
        border: "none",
        borderBottom: "1px solid #999",
        background: "transparent",
        textAlign: "center",
        fontSize: "12px",
        color: "#333",
        outline: "none",
        padding: "0 2px",
        fontFamily: "monospace",
    },
    overlay: {
        position: "absolute",
        top: "0px",
        right: "18px",
        zIndex: 9999,
        width: "360px",
        background: "#f3f3f3",
        border: "1px solid #c8c8c8",
        boxShadow: "0 4px 10px rgba(0, 0, 0, 0.15)",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
        fontSize: "13px",
        color: "#333",
        userSelect: "none",
        padding: "4px 6px 4px 4px",
        display: "flex",
        flexDirection: "column",
        gap: "4px",
    },
    row: {
        display: "flex",
        alignItems: "stretch",
        height: "26px",
        gap: "4px",
    },
    inputWrapper: {
        flex: 1,
        display: "flex",
        background: "#fff",
        border: "1px solid #cecece",
        alignItems: "center",
        transition: "border-color 0.2s",
    },
    input: {
        flex: 1,
        border: "none",
        padding: "0 6px",
        fontSize: "13px",
        outline: "none",
        background: "transparent",
        color: "#333",
    },
    btnGroup: {
        display: "flex",
        background: "#fff",
        border: "1px solid #cecece",
    },
    btn: {
        padding: "0 8px",
        border: "none",
        background: "transparent",
        cursor: "pointer",
        fontSize: "13px",
        color: "#444",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    btnBorderRight: {
        borderRight: "1px solid #cecece",
    },
    closeBtn: {
        background: "transparent",
        border: "none",
        cursor: "pointer",
        padding: "0 4px",
        fontSize: "16px",
        color: "#666",
        display: "flex",
        alignItems: "center",
    },
    footer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "2px 2px 2px 4px",
        fontSize: "12px",
        color: "#666",
    },
    toggleGroup: {
        display: "flex",
        gap: "2px",
    },
    toggleBtn: (active) => ({
        padding: "2px 4px",
        border: "1px solid transparent",
        backgroundColor: active ? "#cce4f7" : "transparent",
        borderColor: active ? "#007fd4" : "transparent",
        borderRadius: "3px",
        cursor: "pointer",
        fontSize: "12px",
        color: "#333",
        fontFamily: "monospace",
    }),
};

export default function WildcardSearch({ aceRef, isOpen, onClose }) {
    const [searchTerm, setSearchTerm] = useState("");
    const [replaceTerm, setReplaceTerm] = useState("");
    const [mode, setMode] = useState("wildcard");
    const [caseSensitive, setCaseSensitive] = useState(false);
    const [wholeWord, setWholeWord] = useState(false);
    const [matchCount, setMatchCount] = useState(null);
    const [currentMatch, setCurrentMatch] = useState(null);
    const [message, setMessage] = useState(null);
    const [sFocus, setSFocus] = useState(false);
    const [rFocus, setRFocus] = useState(false);
    const [regexValid, setRegexValid] = useState(true);

    // ── NEW: controls the editable index input ────────────────────────────
    const [indexInput, setIndexInput] = useState("");  // what user types
    const [indexFocus, setIndexFocus] = useState(false);

    const searchRef = useRef(null);

    const opts = () => ({ mode, caseSensitive, wholeWord });
    const ed = () => aceRef?.current?.editor;

    // ── Focus on open ──────────────────────────────────────────────────────
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => searchRef.current?.focus(), 60);
            setMessage(null);
        }
    }, [isOpen]);

    // ── FIX 1: Recount when searchTerm/options change ──────────────────────
    useEffect(() => {
        if (!isOpen || !searchTerm) {
            setMatchCount(null);
            setCurrentMatch(null);
            setIndexInput("");
            setRegexValid(true);
            return;
        }
        const editor = ed();
        if (!editor) return;

        if (mode === "regex") {
            try { new RegExp(searchTerm); setRegexValid(true); }
            catch { setRegexValid(false); setMatchCount(null); setCurrentMatch(null); return; }
        }

        const n = countMatches(editor, searchTerm, { mode, caseSensitive, wholeWord });
        setMatchCount(n);
        setCurrentMatch(null);
        setIndexInput("");
    }, [searchTerm, mode, caseSensitive, wholeWord, isOpen]);

    // ── FIX 1: Recount whenever editor CONTENT changes ────────────────────
    useEffect(() => {
        if (!isOpen || !searchTerm) return;
        const editor = ed();
        if (!editor) return;

        const handleEditorChange = () => {
            if (mode === "regex") {
                try { new RegExp(searchTerm); }
                catch { setMatchCount(null); setCurrentMatch(null); return; }
            }
            // Fresh count
            const n = countMatches(editor, searchTerm, { mode, caseSensitive, wholeWord });
            setMatchCount(n);
            // Refresh current position
            const info = getCurrentMatchIndex(editor, searchTerm, { mode, caseSensitive, wholeWord });
            setCurrentMatch(info);
            // Keep index input in sync
            if (info) setIndexInput(String(info.current));
        };

        editor.session.on('change', handleEditorChange);
        return () => editor.session.off('change', handleEditorChange);

    }, [isOpen, searchTerm, mode, caseSensitive, wholeWord]);

    // ── Helpers ────────────────────────────────────────────────────────────
    const refreshCurrentIndex = useCallback(() => {
        const editor = ed();
        if (!editor || !searchTerm) return;
        const info = getCurrentMatchIndex(editor, searchTerm, opts());
        setCurrentMatch(info);
        if (info) setIndexInput(String(info.current));
    }, [searchTerm, mode, caseSensitive, wholeWord]);

    const findNext = useCallback(() => {
        const r = wildcardFind(ed(), searchTerm, { ...opts(), backwards: false, wrap: true });
        setMessage(r.found ? null : { text: r.message, err: true });
        if (r.found) refreshCurrentIndex();
    }, [searchTerm, mode, caseSensitive, wholeWord, refreshCurrentIndex]);

    const findPrev = useCallback(() => {
        const r = wildcardFind(ed(), searchTerm, { ...opts(), backwards: true, wrap: true });
        setMessage(r.found ? null : { text: r.message, err: true });
        if (r.found) refreshCurrentIndex();
    }, [searchTerm, mode, caseSensitive, wholeWord, refreshCurrentIndex]);

    const doReplace = useCallback(() => {
        const r = wildcardReplace(ed(), searchTerm, replaceTerm, opts());
        setMessage({ text: r.message, err: !r.replaced });
        const n = countMatches(ed(), searchTerm, opts());
        setMatchCount(n);
        refreshCurrentIndex();
    }, [searchTerm, replaceTerm, mode, caseSensitive, wholeWord, refreshCurrentIndex]);

    const doReplaceAll = useCallback(() => {
        const r = wildcardReplaceAll(ed(), searchTerm, replaceTerm, opts());
        setMessage({ text: r.message, err: r.count === 0 });
        setMatchCount(0);
        setCurrentMatch(null);
        setIndexInput("");
    }, [searchTerm, replaceTerm, mode, caseSensitive, wholeWord]);

    // ── FIX 2: Jump to typed index ─────────────────────────────────────────
    const handleIndexJump = (e) => {
        if (e.key !== 'Enter') return;
        const target = parseInt(indexInput, 10);
        if (isNaN(target)) return;

        const result = jumpToMatchIndex(ed(), searchTerm, target, opts());
        if (result.found) {
            setCurrentMatch({ current: result.current, total: result.total });
            setIndexInput(String(result.current));
        }
    };

    const handleIndexBlur = () => {
        setIndexFocus(false);
        // Jump on blur too so clicking away also navigates
        const target = parseInt(indexInput, 10);
        if (!isNaN(target) && searchTerm) {
            const result = jumpToMatchIndex(ed(), searchTerm, target, opts());
            if (result.found) {
                setCurrentMatch({ current: result.current, total: result.total });
                setIndexInput(String(result.current));
            } else if (currentMatch) {
                // Restore to current if invalid
                setIndexInput(String(currentMatch.current));
            }
        }
    };

    const onKeySearch = (e) => {
        if (e.key === "Enter") { e.preventDefault(); findNext(); }
        if (e.key === "Escape") onClose();
    };
    const onKeyReplace = (e) => {
        if (e.key === "Enter") { e.preventDefault(); doReplace(); }
        if (e.key === "Escape") onClose();
    };

    if (!isOpen) return null;

    // ── Counter label logic ────────────────────────────────────────────────
    const total = currentMatch?.total ?? matchCount ?? 0;

    return (
        <div style={S.overlay} role="dialog" aria-label="Search and Replace">

            {/* ── Search Row ── */}
            <div style={S.row}>
                <div style={{
                    ...S.inputWrapper,
                    borderColor: (!regexValid && searchTerm) ? "#e51400" : (sFocus ? "#007fd4" : "#cecece")
                }}>
                    <input
                        ref={searchRef}
                        style={S.input}
                        placeholder={
                            mode === "regex" ? "Regex search" :
                                mode === "wildcard" ? "Wildcard search" : "Search for"
                        }
                        value={searchTerm}
                        onChange={e => {
                            setSearchTerm(e.target.value);
                            setMessage(null);
                            setCurrentMatch(null);
                            setIndexInput("");
                        }}
                        onKeyDown={onKeySearch}
                        onFocus={() => setSFocus(true)}
                        onBlur={() => setSFocus(false)}
                        spellCheck={false}
                    />
                </div>
                <div style={S.btnGroup}>
                    <button style={{ ...S.btn, ...S.btnBorderRight }} onClick={findPrev} title="Previous">&lt;</button>
                    <button style={S.btn} onClick={findNext} title="Next">&gt;</button>
                </div>
                <button style={S.closeBtn} onClick={onClose}>×</button>
            </div>

            {/* ── Replace Row ── */}
            <div style={S.row}>
                <div style={{ ...S.inputWrapper, borderColor: rFocus ? "#007fd4" : "#cecece" }}>
                    <input
                        style={S.input}
                        placeholder="Replace with"
                        value={replaceTerm}
                        onChange={e => setReplaceTerm(e.target.value)}
                        onKeyDown={onKeyReplace}
                        onFocus={() => setRFocus(true)}
                        onBlur={() => setRFocus(false)}
                        spellCheck={false}
                    />
                </div>
                <div style={S.btnGroup}>
                    <button style={{ ...S.btn, ...S.btnBorderRight }} onClick={doReplace}>Replace</button>
                    <button style={S.btn} onClick={doReplaceAll}>All</button>
                </div>
            </div>

            {/* ── Footer: counter with editable index + toggles ── */}
            <div style={S.footer}>
                <div style={{ display: "flex", alignItems: "center", gap: "3px", fontSize: "12px", color: "#666" }}>
                    {!regexValid && searchTerm ? (
                        <span style={{ color: "#e51400" }}>Invalid</span>
                    ) : total === 0 ? (
                        <span>0 of 0</span>
                    ) : (
                        <>
                            {/* ── FIX 2: Editable index input ── */}
                            <input
                                style={{
                                    ...S.indexInput,
                                    // Grow width with content
                                    width: `${Math.max(20, (indexInput.length || 1) * 9)}px`,
                                    borderBottomColor: indexFocus ? "#007fd4" : "#aaa",
                                }}
                                value={indexFocus ? indexInput : (currentMatch?.current ?? "-")}
                                onChange={e => {
                                    // Only allow digits
                                    const v = e.target.value.replace(/\D/g, '');
                                    setIndexInput(v);
                                }}
                                onFocus={() => {
                                    setIndexFocus(true);
                                    setIndexInput(String(currentMatch?.current ?? 1));
                                }}
                                onBlur={handleIndexBlur}
                                onKeyDown={handleIndexJump}
                                title="Type a number and press Enter to jump"
                            />
                            <span>&nbsp;of {total}</span>
                        </>
                    )}
                </div>

                <div style={S.toggleGroup}>
                    <button
                        style={S.toggleBtn(caseSensitive)}
                        onClick={() => setCaseSensitive(p => !p)}
                        title="Match Case"
                    >
                        Aa
                    </button>
                    <button
                        style={S.toggleBtn(wholeWord)}
                        onClick={() => setWholeWord(p => !p)}
                        title="Match Whole Word"
                    >
                        \b
                    </button>
                </div>
            </div>

            {message && (
                <div style={{
                    fontSize: "11px", padding: "3px 6px",
                    color: message.err ? "#e51400" : "#388a34",
                    borderTop: "1px solid #e8e8e8",
                }}>
                    {message.text}
                </div>
            )}
        </div>
    );
}